import PropTypes from 'prop-types';
import { SimpleForm, TextInput, BooleanInput, required, email, maxLength, SelectInput, useTranslate } from 'react-admin';
import { Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { noSpecialCharacters } from '@/components/global/validators/customValidators.jsx';
import SendMessageIcon from '@/assets/icons/send-message-icon.svg?react';
import TagCategoryToolbar from '@/components/global/tags/TagCategoryToolbar';
import { languageChoices } from '@/components/global/options/selectOptions.jsx';
import { RoleInput } from '@/components/users/forms/RoleInput.jsx';
import { ContractorInput } from '@/components/users/forms/ContractorInput.jsx';
import { ServiceCompanyInput } from '@/components/users/forms/ServiceCompanyInput.jsx';
import { userFormStyles } from '@/themes/commonStyles/user.js';

const UserFormToolbar = ({ handleClose = () => {}, isEdit = false }) => {
    const translate = useTranslate();
    return (
        <Box className='padding-wrapper popup-wrapper-top'>
            <TagCategoryToolbar
                onCancel={handleClose}
                cancelLabel={translate('common.cancel')}
                saveLabel={isEdit ? translate('common.save') : translate('resources.user.labels.invite')}
                saveIcon={isEdit ? null : <SendMessageIcon />}
            />
        </Box>
    );
};

UserFormToolbar.propTypes = {
    handleClose: PropTypes.func,
    isEdit: PropTypes.bool,
};

const StyledUserForm = styled(Box)(userFormStyles);

export const UserForm = ({ isAdmin, validatePhone, handleClose, isEdit = false }) => {
    const translate = useTranslate();
    return (
        <StyledUserForm>
            <SimpleForm toolbar={<UserFormToolbar handleClose={handleClose} isEdit={isEdit} />}>
                <Box className='padding-wrapper'>
                    <Typography variant='h6' gutterBottom>
                        {translate('resources.user.labels.contactSection')}
                    </Typography>
                    <TextInput
                        name='firstName'
                        source='firstName'
                        label={translate('resources.user.fields.firstName')}
                        validate={[
                            required(translate('common.validation.required')),
                            maxLength(150, translate('common.validation.maxLength', { count: 150 })),
                            noSpecialCharacters,
                        ]}
                        fullWidth
                    />
                    <TextInput
                        name='lastName'
                        source='lastName'
                        label={translate('resources.user.fields.lastName')}
                        validate={[
                            required(translate('common.validation.required')),
                            maxLength(150, translate('common.validation.maxLength', { count: 150 })),
                            noSpecialCharacters,
                        ]}
                        fullWidth
                    />
                    <TextInput
                        name='email'
                        source='email'
                        type='email'
                        label={translate('resources.user.fields.email')}
                        validate={[
                            required(translate('common.validation.required')),
                            email(translate('common.validation.invalidEmail')),
                            maxLength(100, translate('common.validation.maxLength', { count: 100 })),
                        ]}
                        fullWidth
                    />
                    <TextInput
                        name='phone'
                        source='phone'
                        type='tel'
                        label={translate('resources.user.fields.phone')}
                        validate={validatePhone}
                        fullWidth
                        placeholder='+48XXXXXXXXX'
                    />
                </Box>
                {isAdmin && (
                    <Box className='padding-wrapper'>
                        <Typography variant='h6' gutterBottom>
                            {translate('resources.user.labels.roleSection')}
                        </Typography>
                        <RoleInput />
                    </Box>
                )}
                {isAdmin && <ContractorInput />}
                {isAdmin && <ServiceCompanyInput />}
                {isAdmin && (
                    <Box className='padding-wrapper'>
                        <Typography variant='h6' gutterBottom>
                            {translate('resources.user.labels.language')}
                        </Typography>
                        <SelectInput
                            source='language'
                            label={translate('resources.user.labels.languageSelect')}
                            choices={languageChoices(translate)}
                            fullWidth
                            validate={required(translate('common.validation.required'))}
                        />
                    </Box>
                )}
                <Box className='padding-wrapper'>
                    <BooleanInput source='isActive' size='small' name='isActive' label={translate('resources.user.fields.isActive')} fullWidth />
                </Box>
            </SimpleForm>
        </StyledUserForm>
    );
};

UserForm.propTypes = {
    isAdmin: PropTypes.bool,
    validatePhone: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
    handleClose: PropTypes.func,
    isEdit: PropTypes.bool,
};
